// Data-layer constants. Every number and path the client and hooks use lives
// here so nothing is hardcoded in call sites (SPEC section 4 rules; api-surface
// sections 1.2, 2.1, 4).

export const API_PATHS = {
  authMe: '/api/beta/auth/me',
  authPrefix: '/api/beta/auth/',
  workspaces: '/api/beta/auth/workspaces',
  workspaceSwitch: '/api/beta/auth/workspaces/switch',
  logout: '/api/beta/auth/logout',
  discordStart: '/api/beta/auth/discord/start',
  login: '/api/beta/auth/login',
  invitationAccept: '/api/beta/auth/invitations/accept',
  emailVerify: '/api/beta/auth/email/verify',
  emailResend: '/api/beta/auth/email/resend',
  passwordRequest: '/api/beta/auth/password/request',
  passwordComplete: '/api/beta/auth/password/complete',
  archiveCalls: '/api/beta/evidence-archive/calls',
  archiveSearch: '/api/beta/evidence-archive/search',
  searchEntities: '/api/beta/search/entities',
  accessChallenge: '/api/beta/auth/access-challenge',
  accessChallengeWorkspace: '/api/beta/auth/access-challenge/workspace',
  accessChallengePassword: '/api/beta/auth/access-challenge/password',
  accessChallengeCancel: '/api/beta/auth/access-challenge/cancel',
  sourceConnectors: '/api/beta/source-connectors',
  healthReady: '/health/ready',
  callTrackerCallers: '/commercial-api/call-tracker/callers',
  callTrackerCalls: '/commercial-api/call-tracker/calls',
  marketRuntime: '/api/beta/market/runtime',
  marketComparison: '/api/beta/market/comparison',
  marketHistoryJobs: '/api/beta/market/history-jobs',
  researchCache: '/commercial-api/research-cache',
  researchRisk: '/commercial-api/research-risk',
  coinSearch: '/commercial-api/coin-search',
  tokenImage: '/commercial-api/token-image',
  alertsSummary: '/api/beta/alerts/summary',
  coinCatalysts: '/api/beta/coin-catalysts',
  coinCatalystsRefresh: '/api/beta/coin-catalysts/refresh',
  coinCatalystsSettings: '/api/beta/coin-catalysts/settings',
  statePrefix: '/api/beta/state/',
} as const;

export function marketOverviewPath(network: string, contract: string): string {
  return `/api/beta/market/${encodeURIComponent(network)}/${encodeURIComponent(contract)}/overview`;
}

export function workspaceStatePath(kind: string): string {
  return `${API_PATHS.statePrefix}${encodeURIComponent(kind)}`;
}

// Cookies (api-surface 1.2). aleph_session is httpOnly and never readable.
export const COOKIE_NAMES = {
  csrf: 'aleph_csrf',
  accessCsrf: 'aleph_access_csrf',
} as const;

export const HEADER_NAMES = {
  csrf: 'X-CSRF-Token',
} as const;

// Window events the client emits.
export const SESSION_EXPIRED_EVENT = 'aleph:session-expired';

// Access check (auth/me): 10 s abort, then three retries at 1, 4 and 10 s.
// The old 250 and 750 ms pair could not outlive a rate-limit window, so a
// single transient 429 or 503 under concurrent load ejected a signed-in owner
// to the sign-in door (docs/DATA-REALITY-2026-08-19, "Load behaviour").
export const ACCESS_CHECK_TIMEOUT_MS = 10_000;
export const ACCESS_CHECK_RETRY_DELAYS_MS: readonly number[] = [1_000, 4_000, 10_000];

// A Retry-After header wins over the schedule above when it asks for longer,
// capped here so a server-chosen window cannot block the boot for minutes.
export const RETRY_AFTER_MAX_MS = 15_000;

// Auth mutations (sign in, invitation, verify, reset): one attempt, 15 s
// abort, so a door never shows an endless spinner.
export const AUTH_REQUEST_TIMEOUT_MS = 15_000;

// Archive calls: the live desk aborts at 20 s (api-surface section 4).
export const ARCHIVE_REQUEST_TIMEOUT_MS = 20_000;

// Polling intervals (api-surface section 4 and contract).
export const POLL_MS = {
  archiveCalls: 10_000,
  sourceConnectors: 2_500,
  healthReady: 15_000,
  marketRuntime: 15_000,
  alertsSummary: 30_000,
  coinCatalysts: 60_000,
} as const;

// Global search (contract 3.2): the suggestions layer waits for the typing to
// settle, needs 2 characters (the server rejects shorter), and asks for a
// short list from each source so the popover stays one screen.
export const SEARCH = {
  minChars: 2,
  maxChars: 120,
  debounceMs: 180,
  entityLimit: 6,
  archiveLimit: 6,
  timeoutMs: 8_000,
} as const;

// Page sizes.
export const ARCHIVE_PAGE_LIMIT = {
  default: 100,
  tape: 30,
  max: 200,
} as const;

// TanStack Query freshness windows.
export const STALE_MS = {
  authMe: 60_000,
  workspaces: 60_000,
  marketOverview: 15_000,
  marketComparison: 60_000,
  callTracker: 30_000,
  researchCache: 30_000,
  workspaceState: 30_000,
  coinCatalysts: 30_000,
} as const;

export function coinCatalystFeedbackPath(eventId: string): string {
  return `${API_PATHS.coinCatalysts}/${encodeURIComponent(eventId)}/feedback`;
}

export function coinCatalystDetailPath(eventId: string): string {
  return `${API_PATHS.coinCatalysts}/${encodeURIComponent(eventId)}`;
}

// Query retry policy: network errors and 5xx get two more tries; 4xx never.
export const QUERY_RETRY_LIMIT = 2;
